import { ReactElement, ReactNode } from 'react';
import { useField } from 'react-final-form';
import { useResolvedOptions } from '@ff-it/ui';
import { useOptions } from 'components/fields/useOptions';
import { useResolveSimple } from 'components/fields/createSelectField';
import { FilterChip } from './FilterChip';
import { humanizeName } from 'utilities';
import { EmbeddedSelect } from 'components/EmbeddedSelect';

const MAX_SHOWN_LABELS = 2;

type EntityFilterChipProps<T> = {
  name: string;
  label?: string;
  url: string;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string;
  formatOptionLabel?: (option: T) => ReactNode;
  filter?: Record<string, unknown>;
  suggestion?: boolean;
  width?: number;
  isMulti?: boolean;
  eager?: boolean;
  defaultValueLabel?: string;
};

export function EntityFilterChip<T>({
  name,
  label,
  url,
  getOptionLabel,
  getOptionValue,
  formatOptionLabel,
  filter,
  suggestion,
  width,
  isMulti,
  eager = false,
  defaultValueLabel,
}: EntityFilterChipProps<T>): ReactElement {
  const { input } = useField(name, { subscription: { value: true } });
  const searchOptions = useOptions<any>(url, filter, { suggestion });
  const resolveSimple = useResolveSimple<T>(url, filter);

  const { resolvedOptions, loadOptions } = useResolvedOptions<any, boolean>({
    loadOptions: searchOptions,
    resolve: resolveSimple,
    value: input.value,
    isMulti,
    getOptionValue,
  });

  const ids: string[] = isMulti
    ? Array.isArray(input.value)
      ? input.value.map(String)
      : []
    : input.value !== undefined && input.value !== null && input.value !== ''
      ? [String(input.value)]
      : [];
  const active = ids.length > 0;

  const findOpt = (id: string): any => resolvedOptions.find((o) => getOptionValue(o) === id);

  let valueNode: ReactNode = null;
  let selectValue: any;
  if (isMulti) {
    selectValue = ids.map(findOpt).filter(Boolean);
    if (active) {
      const labels = ids.map((id) => {
        const o = findOpt(id);
        return o ? getOptionLabel(o) : `#${id}`;
      });
      valueNode =
        labels.length <= MAX_SHOWN_LABELS
          ? labels.join(', ')
          : `${labels.slice(0, MAX_SHOWN_LABELS).join(', ')} +${labels.length - MAX_SHOWN_LABELS}`;
    }
  } else {
    const o = active ? findOpt(ids[0]) : null;
    selectValue = o ?? null;
    valueNode = o ? getOptionLabel(o) : active ? `#${ids[0]}` : null;
  }
  if (!active && defaultValueLabel != null) {
    valueNode = defaultValueLabel;
  }

  return (
    <FilterChip
      label={label ?? humanizeName(name)}
      active={active}
      valueNode={valueNode}
      onClear={() => input.onChange(undefined)}
      width={width}
      flush
    >
      {(close) => (
        <EmbeddedSelect
          cacheOptions
          isMulti={isMulti}
          closeMenuOnSelect={!isMulti}
          loadOptions={loadOptions}
          defaultOptions={eager ? true : resolvedOptions}
          controlShouldRenderValue={false}
          hideSelectedOptions={false}
          backspaceRemovesValue={false}
          tabSelectsValue={false}
          isClearable={false}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          formatOptionLabel={formatOptionLabel}
          value={selectValue}
          onChange={(o: any) => {
            if (isMulti) {
              const arr: any[] = o || [];
              input.onChange(arr.length ? arr.map(getOptionValue) : undefined);
            } else {
              input.onChange(o ? getOptionValue(o) : undefined);
              close();
            }
          }}
        />
      )}
    </FilterChip>
  );
}
